import React from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  Tooltip,
} from '@mui/material';
import { GoGift } from 'react-icons/go';
import { CiLocationOn, CiForkAndKnife } from 'react-icons/ci';
import { IoCheckmarkOutline } from 'react-icons/io5';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { environment } from '../../../../environments/environment';

const RewardOfferCard = ({
  icon,
  tag,
  title,
  description,
  points,
  onRedeem,
  appliedLocations,
  stores,
  productDetails,
  productType,
  productSet,
}) => {
  const navigate = useNavigate();

  const reduxStores = useSelector((state) => state.shopReducer?.stores || []);

  const appliedLocationsDisplay = React.useMemo(() => {
    if (!appliedLocations) return 'All branches';
    if (Array.isArray(appliedLocations) && appliedLocations.length === 0)
      return 'All branches';

    const allStores = (stores && stores.length ? stores : reduxStores) || [];

    const mapped = (appliedLocations || [])
      .map((id) => {
        const s = allStores.find((st) => st?.id === id || st?.storeId === id);
        return s ? s.name || s.businessName || id : id;
      })
      .filter(Boolean);

    return mapped.length > 0 ? mapped.join(', ') : 'All branches';
  }, [appliedLocations, stores, reduxStores]);
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: '1px solid rgba(255,106,0,0.12)',
        boxShadow: '0 6px 20px rgba(16,24,40,0.06)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        background: '#fff',
      }}
    >
      <CardContent sx={{ pb: 2.5, position: 'relative' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                background: 'rgba(255,106,0,0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ff6a00',
              }}
            >
              <GoGift size={24} />
            </Box>
          </Box>

          {tag && (
            <Box
              sx={{
                bgcolor: 'rgba(56, 189, 124, 0.12)',
                color: '#10b981',
                px: 1.25,
                py: 0.5,
                borderRadius: '999px',
                fontSize: 12,
                fontWeight: 600,
                alignSelf: 'flex-start',
              }}
            >
              {tag}
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography
            sx={{ fontSize: 18, fontWeight: 700, color: '#222', mb: 2 }}
          >
            {title}
          </Typography>

          {/* Enhanced branch locations display */}
          {appliedLocations &&
            Array.isArray(appliedLocations) &&
            appliedLocations.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  mb: 1.5,
                  p: 1.5,
                  bgcolor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e9ecef',
                }}
              >
                <Box
                  sx={{
                    width: '22px',
                    height: '22px',
                    borderRadius: '50%',
                    bgcolor: environment.mainColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    mt: 0.2,
                  }}
                >
                  <CiLocationOn size={14} color="white" />
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#495057',
                      fontSize: '11px',
                      fontFamily: 'Jost',
                      fontWeight: 600,
                      lineHeight: 1.3,
                      mb: 0.3,
                    }}
                  >
                    Available Locations
                  </Typography>
                  <Tooltip title={appliedLocationsDisplay} arrow>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#6c757d',
                        fontSize: '10px',
                        fontFamily: 'Jost',
                        fontWeight: 500,
                        lineHeight: 1.4,
                      }}
                    >
                      {appliedLocationsDisplay}
                    </Typography>
                  </Tooltip>
                </Box>
              </Box>
            )}

          {/* Enhanced product applicability */}
          {(() => {
            if (
              productType === 'all' ||
              productSet?.productSetData?.allProducts === true
            ) {
              return (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    p: 1.5,
                    bgcolor: '#e8f5e8',
                    borderRadius: '8px',
                    border: '1px solid #c3e6c3',
                  }}
                >
                  <Box
                    sx={{
                      width: '22px',
                      height: '22px',
                      borderRadius: '50%',
                      bgcolor: '#28a745',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      mt: 0.2,
                    }}
                  >
                    <IoCheckmarkOutline size={14} color="white" />
                  </Box>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#155724',
                        fontSize: '11px',
                        fontFamily: 'Jost',
                        fontWeight: 600,
                        lineHeight: 1.3,
                        mb: 0.3,
                      }}
                    >
                      Item Coverage
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#155724',
                        fontSize: '10px',
                        fontFamily: 'Jost',
                        fontWeight: 500,
                        lineHeight: 1.4,
                      }}
                    >
                      All menu items eligible
                    </Typography>
                  </Box>
                </Box>
              );
            }

            if (Array.isArray(productDetails) && productDetails.length > 0) {
              // Group by type for better display
              const categories = productDetails
                .filter(
                  (detail) =>
                    detail.type === 'category' || detail.rawType === 'CATEGORY'
                )
                .map((detail) => detail.name);

              const items = productDetails
                .filter(
                  (detail) =>
                    detail.type === 'item_variation' ||
                    detail.rawType === 'ITEM_VARIATION'
                )
                .map((detail) => detail.name);

              // Combine results with labels
              const allMatched = [];
              if (categories.length > 0) {
                allMatched.push(`Categories: ${categories.join(', ')}`);
              }
              if (items.length > 0) {
                allMatched.push(`Items: ${items.join(', ')}`);
              }

              const displayText =
                allMatched.length > 0
                  ? allMatched.join(' • ')
                  : productDetails.map((d) => d.name).join(', ');
              const isLongList = displayText.length > 60;
              const truncatedText = isLongList
                ? displayText.substring(0, 60) + '...'
                : displayText;

              return (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    p: 1.5,
                    bgcolor: '#fff3cd',
                    borderRadius: '8px',
                    border: '1px solid #ffeaa7',
                  }}
                >
                  <Box
                    sx={{
                      width: '22px',
                      height: '22px',
                      borderRadius: '50%',
                      bgcolor: '#ffc107',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      mt: 0.2,
                    }}
                  >
                    <CiForkAndKnife size={14} style={{ color: 'white' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#856404',
                        fontSize: '11px',
                        fontFamily: 'Jost',
                        fontWeight: 600,
                        lineHeight: 1.3,
                        mb: 0.3,
                      }}
                    >
                      {categories.length > 0 && items.length > 0
                        ? 'Select Categories & Items'
                        : categories.length > 0
                        ? 'Select Categories'
                        : 'Select Items Only'}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#856404',
                        fontSize: '10px',
                        fontFamily: 'Jost',
                        fontWeight: 500,
                        lineHeight: 1.4,
                      }}
                      title={isLongList ? displayText : undefined}
                    >
                      {truncatedText}
                      {isLongList && (
                        <Typography
                          component="span"
                          sx={{
                            color: '#6c757d',
                            fontSize: '9px',
                            fontStyle: 'italic',
                            ml: 0.5,
                          }}
                        >
                          ({categories.length + items.length} total)
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                </Box>
              );
            }

            return null;
          })()}
        </Box>
      </CardContent>

      <Box
        sx={{
          px: 3,
          pb: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#ff6a00' }}>
          {points} pts
        </Typography>

        <Button
          variant="contained"
          onClick={() => navigate('/menu')}
          sx={{
            bgcolor: '#ff6a00',
            '&:hover': { bgcolor: '#f04200' },

            borderRadius: 2,
            px: 2,
            py: 0.75,
            boxShadow: 'none',
            textTransform: 'capitalize',
          }}
        >
          order now
        </Button>
      </Box>
    </Card>
  );
};

RewardOfferCard.propTypes = {
  icon: PropTypes.node,
  tag: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  points: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onRedeem: PropTypes.func,
  appliedLocations: PropTypes.array,
  stores: PropTypes.array,
  productDetails: PropTypes.array,
  productType: PropTypes.string,
  productSet: PropTypes.object,
};

RewardOfferCard.defaultProps = {
  icon: null,
  tag: '',
  description: '',
  onRedeem: () => undefined,
  stores: [],
};

export default RewardOfferCard;
